import { Box, Stack, Typography, useTheme } from "@mui/material";
import { FoodsTags, ToggleSwitch } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import type { ConsentPreference } from "src/api/client-api/consent/useGetConsentPreferences";
import { ConsentPreviewButton } from "./ConsentPreviewButton";
import { getConsentPreviewText } from "./ConsentDocumentPreviewDialog";
import { formatConsentDateTime } from "./consentDateFormat";
import { getConsentDisplayTitle } from "./consentDisplay";

type ConsentPreferenceRowProps = {
    preference: ConsentPreference;
    isEnabled: boolean;
    isModified: boolean;
    isLast: boolean;
    onPreviewDocument?: () => void;
    onToggle: (enabled: boolean) => void;
};

export const ConsentPreferenceRow = ({
    preference,
    isEnabled,
    isModified,
    isLast,
    onPreviewDocument,
    onToggle,
}: ConsentPreferenceRowProps) => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const locale = i18n?.resolvedLanguage || i18n?.language;
    const previewText = getConsentPreviewText(preference.summary, preference.content);
    const displayTitle = getConsentDisplayTitle(preference.title, preference.version);
    const hasPreviewDocument = Boolean(
        onPreviewDocument &&
        (preference.summary?.trim() || preference.content?.trim() || preference.documentUrl?.trim()),
    );

    return (
        <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            sx={{
                minHeight: "68px",
                px: { xs: theme.spacing(2), md: theme.spacing(4) },
                py: theme.spacing(1.5),
                gap: theme.spacing(1.5),
                borderBottom: isLast ? "none" : `1px solid ${theme.custom.grey[500]}`,
                bgcolor: isModified ? theme.palette.primary[50] : "transparent",
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
                transition: "background-color 0.15s",
                width: "100%",
            }}
        >
            <Stack sx={{ flex: 1, minWidth: 0, maxWidth: "100%", overflow: "hidden", width: "100%", gap: "2px" }}>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography
                        variant="body"
                        sx={{
                            color: theme.custom.grey[2800],
                            fontWeight: 700,
                            maxWidth: "100%",
                            overflowWrap: "anywhere",
                        }}
                    >
                        {displayTitle}
                    </Typography>
                    {preference.version && <FoodsTags label={`v${preference.version}`} variant="tags-grey" />}
                    {isModified && (
                        <FoodsTags label={t("consent.preference.modified", "Modifi\u00e9")} variant="tags-grey" />
                    )}
                </Stack>
                <Typography variant="caption" sx={{ color: theme.custom.grey[2000], display: "block" }}>
                    {preference.updatedAt ?
                        t("consent.preference.answered_on", "Answered on {{date}}", {
                            date: formatConsentDateTime(preference.updatedAt, locale),
                        })
                    :   t("consent.preference.not_answered", "No answer recorded yet")}
                </Typography>
                {previewText && (
                    <Typography
                        variant="body2"
                        noWrap
                        sx={{
                            color: theme.custom.grey[2000],
                            lineHeight: 1.5,
                            maxWidth: "100%",
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            width: "100%",
                        }}
                    >
                        {previewText}
                    </Typography>
                )}
            </Stack>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent={{ xs: "space-between", md: "flex-end" }}
                sx={{
                    gap: theme.spacing(3),
                    flexShrink: 0,
                    width: { xs: "100%", md: "auto" },
                }}
            >
                {hasPreviewDocument && <ConsentPreviewButton onClick={onPreviewDocument} />}
                <Box
                    component="label"
                    sx={{
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 0,
                        "& .MuiSwitch-root": {
                            mr: 0,
                        },
                        "&:focus-visible": {
                            borderRadius: theme.spacing(1),
                            boxShadow: `0 0 0 3px ${theme.palette.primary[50]}`,
                            outline: "none",
                        },
                    }}
                >
                    <ToggleSwitch
                        key={`${preference.itemId}-${isEnabled ? "enabled" : "disabled"}`}
                        defaultChecked={isEnabled}
                        onChange={(e) => onToggle(e.target.checked)}
                        inputProps={{
                            "aria-label":
                                isEnabled ?
                                    t("consent.preference.enabled", "Activ\u00e9")
                                :   t("consent.preference.disabled", "D\u00e9sactiv\u00e9"),
                        }}
                        toggleSize="small"
                        sx={{ m: 0, mr: 0 }}
                    />
                    <Typography
                        variant="body2"
                        noWrap
                        sx={{
                            fontWeight: 700,
                            color: isEnabled ? theme.palette.success.main : theme.custom.grey[2000],
                        }}
                    >
                        {isEnabled ?
                            t("consent.preference.enabled", "Activ\u00e9")
                        :   t("consent.preference.disabled", "D\u00e9sactiv\u00e9")}
                    </Typography>
                </Box>
            </Stack>
        </Stack>
    );
};
